/**
 * 공통 차트 테마 설정 (Watch Data Dashboard 스타일 참조)
 * Chart.js 기반 차트 컴포넌트에서 공통으로 사용하는 스타일 유틸리티
 */
import Chart, { Interaction } from 'chart.js/auto';
import { getRelativePosition } from 'chart.js/helpers';

// === 색상 팔레트 ===
export const COLORS = {
	textPrimary: '#334155',
	textSecondary: '#64748b',
	textMuted: '#94a3b8',
	gridLine: 'rgba(226, 232, 240, 0.6)',
	tooltipBg: 'rgba(10, 10, 20, 0.96)',
	tooltipBorder: 'rgba(99, 102, 241, 0.25)',
	tooltipText: '#cbd5e1',
	tooltipTitle: '#818cf8',
};

// === 캔버스 그라데이션 생성 헬퍼 ===
export function createGradient(ctx, chartArea, colorTop, colorBottom, opacityTop = 0.35, opacityBottom = 0.02) {
	if (!chartArea) return colorTop;
	const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
	gradient.addColorStop(0, hexToRgba(colorTop, opacityTop));
	gradient.addColorStop(1, hexToRgba(colorBottom, opacityBottom));
	return gradient;
}

function hexToRgba(hex, alpha) {
	const r = parseInt(hex.slice(1, 3), 16);
	const g = parseInt(hex.slice(3, 5), 16);
	const b = parseInt(hex.slice(5, 7), 16);
	return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// === 공통 축 스타일 ===
export const axisStyle = {
	ticks: {
		color: COLORS.textSecondary,
		font: { size: 11, family: "'Pretendard', 'DM Sans', system-ui, sans-serif" },
		padding: 6,
	},
	grid: {
		color: COLORS.gridLine,
		drawBorder: false,
		tickLength: 0,
	},
	border: { display: false },
};

// === 공통 X축 (시간축) 스타일 ===
export function timeXAxis(overrides = {}) {
	const { ticks: oTicks, grid: oGrid, border: oBorder, title: oTitle, ...rest } = overrides;
	return {
		type: 'time',
		time: { unit: 'hour', displayFormats: { hour: 'HH:mm' } },
		ticks: { ...axisStyle.ticks, maxTicksLimit: 12, ...oTicks },
		grid: { ...axisStyle.grid, display: false, ...oGrid },
		border: { ...axisStyle.border, ...oBorder },
		title: { display: false, ...oTitle },
		...rest,
	};
}

// === 공통 Y축 스타일 ===
export function valueYAxis(overrides = {}) {
	const { ticks: oTicks, grid: oGrid, border: oBorder, title: oTitle, ...rest } = overrides;
	return {
		ticks: { ...axisStyle.ticks, ...oTicks },
		grid: { ...axisStyle.grid, ...oGrid },
		border: { ...axisStyle.border, ...oBorder },
		title: { display: false, ...oTitle },
		...rest,
	};
}

// === 다크 툴팁 스타일 ===
export const darkTooltip = {
	backgroundColor: COLORS.tooltipBg,
	titleColor: COLORS.tooltipTitle,
	bodyColor: COLORS.tooltipText,
	borderColor: COLORS.tooltipBorder,
	borderWidth: 1,
	cornerRadius: 10,
	padding: { top: 10, bottom: 10, left: 14, right: 14 },
	titleFont: { size: 12, weight: '600' },
	bodyFont: { size: 13 },
	bodySpacing: 6,
	displayColors: true,
	boxWidth: 8,
	boxHeight: 8,
	boxPadding: 4,
	usePointStyle: true,
};

// === 공통 범례 스타일 ===
export const cleanLegend = {
	display: true,
	position: 'top',
	align: 'end',
	labels: {
		color: COLORS.textSecondary,
		font: { size: 11 },
		boxWidth: 12,
		boxHeight: 2,
		padding: 16,
		usePointStyle: false,
	},
};

// === 공통 차트 옵션 베이스 ===
export function baseChartOptions(overrides = {}) {
	return {
		responsive: true,
		maintainAspectRatio: false,
		interaction: { mode: 'timeNearest', intersect: false },
		animation: { duration: 300 },
		transitions: {
			hide: { animation: { duration: 0 } },
			show: { animation: { duration: 0 } },
		},
		layout: { padding: { top: 4, right: 8, bottom: 0, left: 0 } },
		plugins: {
			legend: { display: false },
			tooltip: { ...darkTooltip },
			...overrides.plugins,
		},
		scales: overrides.scales || {},
		...overrides,
	};
}

// === 커스텀 인터랙션 모드: 시간 기반 nearest ===
function binarySearchNearest(data, targetX) {
	if (!data || data.length === 0) return -1;
	let lo = 0, hi = data.length - 1;
	while (lo < hi) {
		const mid = (lo + hi) >> 1;
		if (data[mid].x < targetX) lo = mid + 1;
		else hi = mid;
	}
	if (lo > 0 && Math.abs(data[lo - 1].x - targetX) < Math.abs(data[lo].x - targetX)) {
		return lo - 1;
	}
	return lo;
}

let _timeNearestRegistered = false;
export function registerTimeNearestMode() {
	if (_timeNearestRegistered) return;
	_timeNearestRegistered = true;

	Interaction.modes.timeNearest = function(chart, e, options, useFinalPosition) {
		const position = getRelativePosition(e, chart);
		const xScale = chart.scales.x;
		if (!xScale) return [];
		const hoveredTime = xScale.getValueForPixel(position.x);

		const items = [];
		chart.data.datasets.forEach((dataset, datasetIndex) => {
			const meta = chart.getDatasetMeta(datasetIndex);
			if (!meta.visible) return;

			const data = dataset.data;
			if (!data || data.length === 0) return;

			const firstTime = data[0].x;
			const lastTime = data[data.length - 1].x;
			if (hoveredTime < firstTime || hoveredTime > lastTime) return;

			const idx = binarySearchNearest(data, hoveredTime);
			if (idx >= 0 && idx < meta.data.length) {
				items.push({ element: meta.data[idx], datasetIndex, index: idx });
			}
		});

		return items;
	};
}
